<template>

    <ErrorSummary :response-status="responseStatus"/>

    <RadioGroup v-model="profileVisibleTo" @update:modelValue="value => updateProfileVisibilitySettings(value)">
        <RadioGroupLabel class="text-lg font-medium text-gray-900">Who can view your profile?</RadioGroupLabel>

        <SkeletonCard  class="mt-4" v-if="isFetching" card-style="long" :num-of-cards=2 />
        
        <div v-else class="grid grid-cols-1 mt-4 gap-y-6 sm:grid-cols-3 sm:gap-x-4">
            <RadioGroupOption as="template"
                              v-for="setting in visibilitySettingOptions"
                              :key="setting.value"
                              :value="setting.value" v-slot="{ checked, active }">
                <div :class="[checked ? 'border-transparent' : 'border-clay-500', active ? 'ring-2 ring-powder-500 bg-powder-50' : '', 'relative flex cursor-pointer rounded-lg border p-4 shadow-sm focus:outline-hidden']">
                                                    <span class="flex flex-1">
                                                      <span class="flex flex-col">
                                                        <RadioGroupLabel as="span" class="block text-sm font-medium text-gray-900">{{ setting.name }}</RadioGroupLabel>
                                                        <RadioGroupDescription as="span" class="items-center mt-2 text-xs text-clay-800">{{ setting.description }}</RadioGroupDescription>
                                                      </span>
                                                    </span>
                    <CheckCircleIcon v-if="checked" class="w-5 h-5 text-powder-600" aria-hidden="true" />
                    <span :class="[active ? 'border' : 'border-2', checked ? 'border-powder-500' : 'border-transparent', 'pointer-events-none absolute -inset-px rounded-lg']" aria-hidden="true" />
                </div>
            </RadioGroupOption>
        </div>
    </RadioGroup>

    <NotificationSuccess :show="updateSuccessful" content="Your changes were successfully saved."
                         title="Successfully saved!"/>

</template>

<script lang="ts" setup>
// @ts-ignore
import {defineProps, onMounted, Ref, ref} from "vue";
import {client, trackEvent} from '@/shared';
import { RadioGroup, RadioGroupDescription, RadioGroupLabel, RadioGroupOption } from '@headlessui/vue';
import { CheckCircleIcon } from '@heroicons/vue/20/solid';
import {
    GetProfileVisibility,
    GetProfileVisibilityResponse,
    UpdateProfileVisibility
} from "@/dtos";
import NotificationSuccess from "@/main/components/NotificationSuccess.vue";
import ErrorSummary from "@/main/components/ErrorSummary.vue";
import SkeletonCard from "@/main/components/skeletons/SkeletonCard.vue";

onMounted(async () => {
    await getProfileVisibilitySettings();
});

const visibilitySettingOptions = ref([
    { name: "Anyone", description: "Your profile is visible to everyone on the internet, including StageAgent members and visitors.",  value: "Anyone"},
    { name: "StageAgent Members", description: "Your profile is only visible only to logged-in StageAgent members.", value: "StageAgent Members"},
    { name: "Private", description: "Your profile is not publicly visible. Only you can see your profile.", value: "Private"}
]);

const isFetching = ref(true);

const profileVisibleTo = ref("");
const getProfileVisibilitySettings = async () => {
    await client.get<GetProfileVisibilityResponse>(new GetProfileVisibility())
        .then(response => {
            profileVisibleTo.value = response.visibleTo;
            isFetching.value = false;
        });
}

const updateSuccessful: Ref<Boolean> = ref(false);
const responseStatus = ref(null);

const updateProfileVisibilitySettings = async (visibleTo: string) => {
    updateSuccessful.value = false;
    await client.post(new UpdateProfileVisibility({
        visibleTo : profileVisibleTo.value
    }))
        .then(() => {
            updateSuccessful.value = true;
            responseStatus.value = null;

            trackEvent( 'profile_updated', {
                method: 'visibility_updated',
                visible_to: profileVisibleTo.value
            });
        })
        .catch(e => {
            responseStatus.value = e.responseStatus;
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        });
};

</script>